// Settings → Concierge → "Resident portal pages": which audience chips may see each portal page.
// Empty = every resident. Saves through the tenant config (`portal_pages`).
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LayoutPanelLeft, Wrench, CalendarCheck, CalendarDays, BarChart3, FileText, Contact, Loader2 } from "lucide-react";
import { useDash } from "@/pages/dashboard/ctx";
import { AudiencePicker } from "@/components/AudiencePicker";
import { V } from "@/lib/vocab";
import { SettingsSection } from "@/components/SettingsSection";

const PAGES = [
  { key: "requests", label: "My Requests", icon: Wrench },
  { key: "amenities", label: "Amenities", icon: CalendarCheck },
  { key: "events", label: "Events", icon: CalendarDays },
  { key: "surveys", label: "Surveys", icon: BarChart3 },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "contacts", label: "Trusted Pros", icon: Contact },
];

export function PortalPagesCard() {
  const { tenantId, tenant, role, refreshTenants } = useDash();
  const canEdit = role !== "board_member";
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState("");
  useEffect(() => { setCfg((tenant && tenant.portal_pages) || {}); }, [tenant]);
  if (!tenantId || cfg === null) return null;

  const save = async (key, chips) => {
    const next = { ...cfg, [key]: chips.includes("anyone") ? [] : chips };
    setCfg(next); setSaving(key);
    try {
      await api.put(`/tenants/${tenantId}/config`, { portal_pages: next });
      toast.success(next[key].length ? `${PAGES.find((p) => p.key === key).label}: limited` : `${PAGES.find((p) => p.key === key).label}: every ${V.lc("person")}`);
      refreshTenants && refreshTenants();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(""); }
  };

  const limited = PAGES.filter((p) => cfg[p.key] && cfg[p.key].length && !(cfg[p.key].length === 1 && cfg[p.key][0] === "anyone")).length;
  return (
    <SettingsSection id="portal-pages-card" icon={LayoutPanelLeft} tone="violet" title={`${V.one("person")} portal pages — who sees which page`}
      badges={[{ text: limited ? `${limited} of ${PAGES.length} limited` : "all open" }]}
      summary={`Which portal pages (${PAGES.map((p) => p.label).join(", ")}) each audience chip can see.`}>
      <p className="text-sm text-muted-foreground -mt-1">
        Same chips as the knowledge base. <b>Anyone</b> means every {V.lc("person")} with a portal login. Limit a page and it disappears from the
        sidebar — and from the URL — for {V.lcs("person")} who don't carry one of the chips.
      </p>
      <div className="rounded-xl border border-sand-200 overflow-hidden divide-y divide-sand-100">
        {PAGES.map((p) => {
          const chips = cfg[p.key] && cfg[p.key].length ? cfg[p.key] : ["anyone"];
          return (
            <div key={p.key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3" data-testid={`portal-page-row-${p.key}`}>
              <div className="flex items-center gap-2 sm:w-44 shrink-0">
                <p.icon className="h-4 w-4 text-ink/60" strokeWidth={1.75} />
                <span className="font-semibold text-sm">{p.label}</span>
                {saving === p.key && <Loader2 className="h-3.5 w-3.5 animate-spin text-ink/40" />}
              </div>
              <div className="flex-1 min-w-0">
                <AudiencePicker tenantId={tenantId} audiences={chips} onAudiences={(c) => save(p.key, c)} readOnly={!canEdit} compact testid={`portal-page-${p.key}`} excludeSources={["vendor_roster", "manager_login"]} />
              </div>
            </div>
          );
        })}
      </div>
    </SettingsSection>
  );
}
